import { computed } from "vue";
import { useModbusAddress } from "./useModbusAddress";
import { ParamField } from "../enums/ParamField";
import { useModbusStore } from "@/stores/useModbusStore";
import { SensorUnitInfo } from "@/types/SensorUnitInfo";
import { SensorCode } from "@/types/sensor/SensorCode";

export function useSensorUnits() {
  const modbusStore = useModbusStore();
  const { modbusAddress } = useModbusAddress();

  function getRSUnitInfo() {
    const unitInfo = modbusStore.guiUnits.find(
      (item) =>
        item.unit_type === "Gauge pressure" &&
        item["No."] ===
          modbusStore.modbusValues[
            modbusAddress.referencePressureUnit
          ]?.toString(),
    );

    return {
      displaySymbol: unitInfo?.Symbol,
      fromUnit: "millibar gauge",
      toUnit: unitInfo?.name,
    };
  }

  function getSensorABCUnitInfo(
    processTypeAddress: number,
    sensorUnitAddress: number,
  ): SensorUnitInfo {
    const process_items = modbusStore.params.find(
      (item) => item[ParamField.mbusAddress] === String(processTypeAddress),
    )?.input_info;
    const process_type =
      process_items?.split("|")[modbusStore.modbusValues[processTypeAddress]];
    const unitInfo = modbusStore.guiUnits.find(
      (item) =>
        item.unit_type == process_type &&
        item["No."] == modbusStore.modbusValues[sensorUnitAddress]?.toString(),
    );
    const deviceUnitInfo = modbusStore.deviceUnits.find(
      (item) => item.unit_type == unitInfo?.unit_type,
    );
    const deviceUnitSymbol = deviceUnitInfo?.device_unit;
    const deviceUnitItem = modbusStore.guiUnits.find(
      (item) => item.Symbol == deviceUnitSymbol,
    );
    return {
      displaySymbol: unitInfo?.Symbol,
      fromUnit:
        process_type === "Temperature" ? "Celsius" : deviceUnitItem?.name,
      toUnit: unitInfo?.name,
    };
  }

  function convertUnit(val: number, sensorCode: SensorCode): number {
    if (!sensorUnitInfo.value[sensorCode]) {
      return val;
    }
    return modbusStore.convert(
      val,
      sensorUnitInfo.value[sensorCode].fromUnit,
      sensorUnitInfo.value[sensorCode].toUnit,
    );
  }

  const sensorUnitInfo = computed<Record<SensorCode, SensorUnitInfo>>(() => ({
    RS: getRSUnitInfo(),
    ISA: getSensorABCUnitInfo(
      modbusAddress.sensorAProcessType,
      modbusAddress.sensorAUnit,
    ),
    ISB: getSensorABCUnitInfo(
      modbusAddress.sensorBProcessType,
      modbusAddress.sensorBUnit,
    ),
    ISC: getSensorABCUnitInfo(
      modbusAddress.sensorCProcessType,
      modbusAddress.sensorCUnit,
    ),
    SP: {},
    IV: {},
    EV: {},
    IEV: {},
    DT: {
      // displaySymbol: "°C",
    },
  }));

  function fetchSensorUnits(deviceAddress: number) {
    const {
      referencePressureUnit,
      sensorAProcessType,
      sensorBProcessType,
      sensorCProcessType,
      sensorAUnit,
      sensorBUnit,
      sensorCUnit,
    } = modbusAddress;

    // NOTE: Triple reads were already present in the root commit (dated 2023-04-17),
    // and HistoryDataView.vue (lines 1593-1595) also does triple reads.
    // We therefore assume that this is likely intentional, though the reason is undocumented.
    // We suspect this is either:
    // - A hardware requirement for the modbus device to stabilize readings
    // - A workaround for timing/synchronization issues
    // Until we can verify with hardware testing, we're keeping this pattern.

    // First read
    modbusStore.modbusRead(deviceAddress, referencePressureUnit, 1);
    modbusStore.modbusRead(deviceAddress, sensorAProcessType, 1);
    modbusStore.modbusRead(deviceAddress, sensorAUnit, 1);
    modbusStore.modbusRead(deviceAddress, sensorBProcessType, 1);
    modbusStore.modbusRead(deviceAddress, sensorBUnit, 1);
    modbusStore.modbusRead(deviceAddress, sensorCProcessType, 1);
    modbusStore.modbusRead(deviceAddress, sensorCUnit, 1);

    // Second read
    modbusStore.modbusRead(deviceAddress, referencePressureUnit, 1);
    modbusStore.modbusRead(deviceAddress, sensorAProcessType, 1);
    modbusStore.modbusRead(deviceAddress, sensorAUnit, 1);
    modbusStore.modbusRead(deviceAddress, sensorBProcessType, 1);
    modbusStore.modbusRead(deviceAddress, sensorBUnit, 1);
    modbusStore.modbusRead(deviceAddress, sensorCProcessType, 1);
    modbusStore.modbusRead(deviceAddress, sensorCUnit, 1);

    // Third read
    modbusStore.modbusRead(deviceAddress, referencePressureUnit, 1);
    modbusStore.modbusRead(deviceAddress, sensorAProcessType, 1);
    modbusStore.modbusRead(deviceAddress, sensorAUnit, 1);
    modbusStore.modbusRead(deviceAddress, sensorBProcessType, 1);
    modbusStore.modbusRead(deviceAddress, sensorBUnit, 1);
    modbusStore.modbusRead(deviceAddress, sensorCProcessType, 1);
    modbusStore.modbusRead(deviceAddress, sensorCUnit, 1);
  }

  return { sensorUnitInfo, convertUnit, fetchSensorUnits };
}
